import { z } from "zod";
import { KycOrgMode } from "./KycOrgForm.types";

const req = z.string().min(1, "Påkrevd");
const opt = z.string().optional().default("");
const optNum = z.number().min(0).optional().default(0);
const optBool = z.boolean().optional().default(false);

const benOwnerSchema = z.object({
  ssn: opt,
  name: opt,
  ownership: optNum,
  votingRights: optNum,
  pep: opt,
});

export function createKycOrgSchema(mode: KycOrgMode = "combined") {
  const kycReq = mode === "mifid" ? opt : req;
  const mifidReq = mode === "kyc" ? opt : req;

  return z.object({
    // ── Selskapsdata ──────────────────────────────────────────────────────────
    orgNumber: z.string(),
    orgName: z.string(),
    address: req,
    zip: req,
    city: req,
    phone: req,
    email: req,
    taxCountry: req,

    // ── Daglig kontakt ────────────────────────────────────────────────────────
    mcId: opt,
    mcName: opt,
    mcAddress: opt,
    mcZip: opt,
    mcCity: opt,
    mcPhone: opt,
    mcEmail: opt,
    amlContactPersonPowerOfAttorney: optBool,
    amlContactPersonPep: optBool,

    // ── Reelle rettighetshavere ───────────────────────────────────────────────
    amlBeneficialOwner1: benOwnerSchema,
    amlBeneficialOwner2: benOwnerSchema,
    amlBeneficialOwner3: benOwnerSchema,
    amlBeneficialOwner4: benOwnerSchema,
    amlBeneficialOwner5: benOwnerSchema,
    benOwnerComment: opt,

    // ── AML/KYC ───────────────────────────────────────────────────────────────
    amlBusinessActivity: kycReq,
    amlBusinessActivityCustom: opt,
    amlPurposeOfCustomerRelationship: kycReq,
    amlPurposeOfCustomerRelationshipCustom: opt,
    amlInvestedPortfolio: kycReq,
    amlAnnualDeposits: kycReq,
    amlSourceOfFunds: kycReq,
    amlSourceOfFundsCustom: opt,
    amlSourceOfWealth: opt,
    amlManagedOnBehalfOfOthers: kycReq,
    amlTransfersAbroad: kycReq,
    amlTransfersAbroadCustom: opt,
    amlCryptoCurrency: kycReq,

    // ── MIFID – Kunnskap og erfaring ──────────────────────────────────────────
    mifidKnowledgeOfMutualFunds: mifidReq,
    mifidExperienceOfMutualFunds: mifidReq,
    mifidTradingFrequencyOfMutualFunds: mifidReq,
    mifidPeriodOfMutualFunds: mifidReq,
    mifidKnowledgeOfListedStocks: mifidReq,
    mifidExperienceOfListedStocks: mifidReq,
    mifidTradingFrequencyOfListedStocks: mifidReq,
    mifidPeriodOfListedStocks: mifidReq,
    mifidKnowledgeOfUnlistedStocks: mifidReq,
    mifidExperienceOfUnlistedStocks: mifidReq,
    mifidTradingFrequencyOfUnlistedStocks: mifidReq,
    mifidPeriodOfUnlistedStocks: mifidReq,
    mifidKnowledgeOfBonds: mifidReq,
    mifidExperienceOfBonds: mifidReq,
    mifidTradingFrequencyOfBonds: mifidReq,
    mifidPeriodOfBonds: mifidReq,
    mifidKnowledgeOfComplexInvestments: mifidReq,
    mifidExperienceOfComplexInvestments: mifidReq,
    mifidTradingFrequencyOfComplexInvestments: mifidReq,
    mifidPeriodOfComplexInvestments: mifidReq,
    mifidRelevantExperienceEducation: mifidReq,

    // ── MIFID – Finansiell situasjon ──────────────────────────────────────────
    mifidTotalNetWealth: opt,
    mifidLiquidInvestableWealth: opt,
    mifidGrossAnnualIncome: opt,
    mifidLiquidityBuffer: opt,
    mifidDebtBurden: opt,
    mifidIncomeStability: mifidReq,
    mifidEarliestWithdrawal: mifidReq,

    // ── MIFID – Risiko og formål ──────────────────────────────────────────────
    mifidMainInvestmentPurpose: mifidReq,
    mifidRiskToleranceVolatility: mifidReq,
    mifidLiquidityNeed: mifidReq,
    mifidMarketDropResponse: mifidReq,
    mifidValueDeclineImpact: mifidReq,

    // ── MIFID – Bærekraft ─────────────────────────────────────────────────────
    mifidEsgPreference: mifidReq,
    mifidEsgPreferenceType: opt,
  });
}

export const kycOrgSchema = createKycOrgSchema("combined");
export type KycOrgData = z.infer<typeof kycOrgSchema>;
export type BenOwnerData = z.infer<typeof benOwnerSchema>;
